"use client";

/**
 * useUpload — orchestrator hook for the upload flow.
 *
 * Manages the file list and coordinates validation → pricing → submission.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/api/client";
import { MediaProcessType } from "@speakai/shared";
import { TRANSLATION_LANGUAGES } from "@/lib/utils/language";
import { useAuth } from "@/hooks/use-auth";
import {
  useUploadValidation,
  type UploadFileItem,
} from "./use-upload-validation";
import { useUploadPricing } from "@/features/subscription/hooks/use-upload-pricing";
import { useSubscription } from "@/features/subscription/queries";
import { useUrlImport, type UrlImportItem } from "./use-url-import";
import { uploadFileToS3 } from "@/features/upload/cognito-upload";
import { FREE_PLAN_MAX_FILE_SIZE } from "@/features/upload/supported-formats";
import { sanitizeFileName } from "@/features/upload/s3-upload";
import { calculateTranslationCost, calculateDubbingCost } from "../cost-calculator";
import { track, trackFunnel, trackFailure } from "@/lib/integrations/sentry-tracking";
import { trackActivity } from "@/lib/integrations/activity-events";
import { sizeBucket } from "@speakai/common/src/observability/activity-events";
import { trackUploadComplete, trackUploadFail, trackUploadStart, trackUploadAbandon, pushDataLayer } from "@/lib/integrations/ga4";

const PAID_PLAN_MIN_FILE_SIZE_GB = 10;

/**
 * Upload-flow state machine.
 *
 *  idle                — no files selected; initial state.
 *  files_selected      — at least one valid file/URL queued; cost not reviewed yet.
 *  validating          — file validation in progress.
 *  uploading           — S3 PUT in progress.
 *  awaiting_payment    — files uploaded; waiting for the user to pay.
 *  topup_required      — balance insufficient; top-up needed before processing.
 *  add_payment_method  — no saved method; user must add card or connect PayPal.
 *  charge_confirm      — user reviewing the exact charge before confirming.
 *  charge_processing   — charge API call in flight.
 *  charge_failed       — charge API returned an error; user can retry or cancel.
 *  direct_deposit_gate — direct-deposit workspaces submitting; blocks self-serve paths.
 *  processing          — server-side analysis queued.
 *  complete            — terminal success.
 *  error               — terminal failure.
 */
type UploadStatus =
  | "idle"
  | "files_selected"
  | "direct_deposit_gate"
  | "topup_required"
  | "add_payment_method"
  | "charge_confirm"
  | "charge_processing"
  | "charge_failed"
  | "validating"
  | "uploading"
  | "awaiting_payment"
  | "processing"
  | "complete"
  | "error";

interface UploadMediaPayload {
  fileName: string;
  mediaType: string;
  folderId: string;
  sourceLanguage: string;
  duration: number;
  fileSize: number;
  extension: string;
  aws: { bucket: string; key: string; location: string };
  uploadSource: string;

  // Translation fields — set only when mode === "translate". Cost fields are
  // GROSS values; the server applies the freeCharacters quota at the transaction
  // layer. Do NOT add a per-file `freeCharactersAllocated` — the server doesn't
  // expect it.
  targetLanguage?: string;
  processType?: MediaProcessType.TRANSLATION;
  enableDubbing?: boolean;
  transcriptionCost?: number;
  translationCost?: number;
  dubbingCost?: number;
  characters?: number;
  amount?: number;
}

interface ProcessedFile {
  id: string;
  mediaId?: string;
  mediaType: string;
  isPaid?: boolean;
}

/**
 * Canonicalises a URL for deduplication. Lowercases scheme + host only
 * (case-insensitive per RFC 3986 §6.2.2.1); path and query preserved as-is.
 * Fragment is dropped — it identifies client-side state, not server resources.
 * Falls back to full lowercase of the raw string if URL parsing fails.
 */
function canonicalizeUrl(raw: string): string {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    if (!["http:", "https:"].includes(u.protocol)) return "";
    return `${u.protocol.toLowerCase()}//${u.host.toLowerCase()}${u.pathname}${u.search}`;
  } catch {
    return raw.toLowerCase();
  }
}

const ALLOWED_URL_MEDIA_TYPES = new Set(["audio", "video"]);

const UPLOAD_CONCURRENCY = 3;

interface UseUploadOptions {
  mode?: "upload" | "translate";
  targetLanguage?: string;
  /** When set, new files and URL items pre-fill targetLanguage with this code. */
  defaultTargetLanguage?: string;
  /** onboarding goal for funnel segmentation (upload-page only). */
  goal?: string | null;
  /** Enables upload-funnel GA4 events. Default false so shared consumers (onboarding) don't pollute the funnel. */
  enableFunnelTracking?: boolean;
}

export function useUpload({
  mode = "upload",
  targetLanguage,
  defaultTargetLanguage = "",
  goal,
  enableFunnelTracking = false,
}: UseUploadOptions = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: subscription } = useSubscription();
  const { t } = useTranslation();
  const { validateFile, validateBatch } = useUploadValidation({ mode, defaultTargetLanguage });
  const pricing = useUploadPricing();
  const paidPlanMaxFileSize =
    Math.max(subscription?.features?.import?.fileSizeInGB ?? 0, PAID_PLAN_MIN_FILE_SIZE_GB) * 1024 ** 3;
  const urlImport = useUrlImport(pricing.insightCost, defaultTargetLanguage);

  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const [urlItems, setUrlItems] = useState<UrlImportItem[]>([]);
  const [status, setStatus] = useState<UploadStatus>("idle");
  const uploadStartTrackedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [folderId, setFolderId] = useState<string>("");
  const [sourceLanguage] = useState<string>(
    mode === "translate"
      ? "auto"
      : user?.onboarding?.sourceLanguage || TRANSLATION_LANGUAGES[0]?.transcriptionCode || "",
  );
  const [processedFiles, setProcessedFiles] = useState<ProcessedFile[]>([]);
  const [uploadedMediaIds, setUploadedMediaIds] = useState<
    Array<{ mediaId: string; name: string }>
  >([]);

  // Prevents concurrent duplicate URL submits.
  const inFlightUrls = useRef<Set<string>>(new Set());

  // file.id → abort() handle returned by uploadFileToS3.
  const abortRegistryRef = useRef<Map<string, () => void>>(new Map());

  // SSR-safe constant; useEffect syncs from navigator.onLine post-mount to avoid
  // hydration mismatch when an offline user opens the page.
  const [isOnline, setIsOnline] = useState(true);
  useEffect(() => {
    if (typeof navigator !== "undefined") setIsOnline(navigator.onLine);
  }, []);
  // True when a disconnect occurred during an active upload session so the
  // auto-resume path knows it needs to re-queue pending files on reconnect.
  const wasOfflineRef = useRef(false);
  // Auto-resume backoff: 2s → 4s → 8s → 16s cap.
  const resumeBackoffCountRef = useRef(0);

  // Reentrancy guard against a concurrent uploadFilesOnly() call.
  const isUploadingRef = useRef(false);

  const resumeTimeoutRef = useRef<number | null>(null);

  // The online/offline listeners below are installed once (empty deps) so
  // they aren't torn down and reinstalled on every files/status change —
  // these refs give that long-lived closure a way to read CURRENT state
  // instead of whatever was current when the effect last ran.
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const { calculatePricing, currentBalance } = pricing;
  const allItems = useMemo(() => [...files, ...urlItems], [files, urlItems]);
  const pricingSummary = useMemo(
    () => calculatePricing(allItems, user?.isCompanyDomain ?? false),
    [calculatePricing, allItems, user?.isCompanyDomain]
  );

  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      if (pricingSummary.isExpired) {
        setErrorMessage(t("UPLOAD_FILE.EXPIRED_ERROR"));
        return;
      }

      setErrorMessage("");
      const newFiles: UploadFileItem[] = [];
      const errors: string[] = [];

      for (const file of Array.from(fileList)) {
        const result = await validateFile(file, {
          isFreePlan: pricing.isFreePlan,
          insightCost: pricing.insightCost,
          folderId,
          sourceLanguage,
          maxFileSize: pricing.isFreePlan ? null : paidPlanMaxFileSize,
        });

        if (result.isValid && result.fileItem) {
          newFiles.push(result.fileItem);
        } else {
          errors.push(`${result.errorMessage} - ${file.name}`);
          if (enableFunnelTracking && result.rejectionReason) {
            pushDataLayer("Upload", "File Rejected", result.rejectionReason, 0, {
              file_type: file.type || "unknown",
            });
          }
        }
      }

      if (errors.length > 0) {
        setErrorMessage(errors.join("\n"));
      }

      if (newFiles.length > 0) {
        setFiles((prev) => [...prev, ...newFiles]);
        setStatus((prev) => (prev === "idle" ? "files_selected" : prev));
      }
    },
    [
      pricingSummary.isExpired,
      validateFile,
      pricing.isFreePlan,
      pricing.insightCost,
      paidPlanMaxFileSize,
      folderId,
      sourceLanguage,
      enableFunnelTracking,
    ]
  );

  const importFromUrl = useCallback(
    async (url: string) => {
      if (pricingSummary.isExpired) {
        setErrorMessage(t("UPLOAD_FILE.EXPIRED_ERROR"));
        return;
      }

      // Gate against the server's supported-platform list before posting so
      // unsupported URLs surface a friendly message instead of a server 400.
      // Only gate once the platform list has loaded — otherwise fall through to
      // the server so a slow platforms fetch can't reject a valid URL.
      if (urlImport.platformsLoaded && !urlImport.validateUrl(url).isValid) {
        setErrorMessage(t("UPLOAD_FILE.URL_INVALID"));
        return;
      }

      const canonicalKey = canonicalizeUrl(url);

      // Already in the queue.
      if (urlItems.some((item) => canonicalizeUrl(item.originalUrl) === canonicalKey)) {
        setErrorMessage(t("UPLOAD_FILE.DUPLICATE_URL"));
        return;
      }

      // Concurrent submit for the same URL.
      if (inFlightUrls.current.has(canonicalKey)) {
        setErrorMessage(t("UPLOAD_FILE.DUPLICATE_URL"));
        return;
      }

      // Mark in-flight synchronously before the first await.
      inFlightUrls.current.add(canonicalKey);
      setErrorMessage("");

      let result: Awaited<ReturnType<typeof urlImport.importUrl>>;
      try {
        result = await urlImport.importUrl(url, folderId, sourceLanguage);
      } finally {
        // Always clear so a network error doesn't lock the URL out permanently.
        inFlightUrls.current.delete(canonicalKey);
      }

      if (result.error) {
        setErrorMessage(result.error);
      }

      if (result.items.length > 0) {
        // Re-check against latest state inside the updater — guards against
        // StrictMode double-invocation and future concurrent renders.
        setUrlItems((prev) => {
          const existingKeys = new Set(prev.map((item) => canonicalizeUrl(item.originalUrl)));
          const deduped = result.items.filter(
            (item) => !existingKeys.has(canonicalizeUrl(item.originalUrl))
          );
          return deduped.length > 0 ? [...prev, ...deduped] : prev;
        });
      }
    },
    [pricingSummary.isExpired, urlImport, folderId, sourceLanguage, urlItems]
  );

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const removeUrlItem = useCallback((itemId: string) => {
    setUrlItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const updateFile = useCallback(
    (
      fileId: string,
      updates: Partial<
        Pick<
          UploadFileItem,
          "fileName" | "sourceLanguage" | "targetLanguage" | "folderId" | "enableDubbing"
        >
      >,
    ) => {
      setFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f))
      );
    },
    []
  );

  const updateUrlItem = useCallback(
    (
      itemId: string,
      updates: Partial<
        Pick<
          UrlImportItem,
          "sourceLanguage" | "targetLanguage" | "folderId" | "enableDubbing"
        >
      >,
    ) => {
      setUrlItems((prev) =>
        prev.map((u) => (u.id === itemId ? { ...u, ...updates } : u))
      );
    },
    []
  );

  const processMedia = useCallback(
    async (filesToProcess: ProcessedFile[]) => {
      setStatus("processing");

      try {
        const processRes = await apiClient.post<
          Array<{ mediaId: string; mediaType: string; name?: string }>
        >("/media/process/file", filesToProcess.map((p) => ({ ...p, isPaid: true })));

        if (processRes.success && processRes.data) {
          const mediaIds = processRes.data.map((m) => ({
            mediaId: m.mediaId,
            name: m.name || "file",
          }));
          setUploadedMediaIds(mediaIds);

          // Kick off the AI pipeline. Without this call the server doc stays in "preparing" forever.
          const analyzePayload = processRes.data.map((m) => ({
            mediaId: m.mediaId,
            mediaType: m.mediaType,
          }));
          const analyzeRes = await apiClient.post<unknown>("/media/analyze", analyzePayload);

          if (!analyzeRes.success) {
            // Charge already succeeded and the doc exists — surface a distinct
            // error so the user knows the upload landed but analysis didn't.
            setStatus("error");
            setErrorMessage(
              t(
                "UPLOAD_FILE.ANALYZE_FAILED_ERROR",
                "Payment succeeded and your file is uploaded, but analysis didn't start. Contact support.",
              ),
            );
            return;
          }
          trackActivity("transcription.start", { mode });
        }

        setStatus("complete");
      } catch {
        setStatus("error");
        setErrorMessage(t("UPLOAD_FILE.PROCESS_FAILED_ERROR"));
      }
    },
    [t, mode]
  );

  /**
   * Upload files to S3 + register with the server. Runs before any payment
   * gate. Bounded-parallel queue of UPLOAD_CONCURRENCY tasks; per-file errors
   * do NOT abort the queue — sibling files continue.
   */
  const uploadFilesOnly = useCallback(async (creditAmountByFileId?: Record<string, number>): Promise<ProcessedFile[]> => {
    if (files.length === 0 && urlItems.length === 0) return [];
    if (!user) return [];
    if (isUploadingRef.current) return [];
    isUploadingRef.current = true;

    try {
      setStatus("uploading");
      setErrorMessage("");
      const uploadStartedAt = Date.now();
      const bucket = sizeBucket(files.reduce((sum, f) => sum + (f.file?.size ?? 0), 0));
      trackActivity("upload.start", { mode, sizeBucket: bucket });
      // Offline auto-resume re-enters here; count the batch once.
      if (!uploadStartTrackedRef.current) {
        uploadStartTrackedRef.current = true;
        trackUploadStart(files.length + urlItems.length, mode, goal);
      }

      const processed: ProcessedFile[] = [];

      try {
        // URL items: sequential — typically few — and skip ones already POSTed
        // so retry/resume after success doesn't re-create them server-side.
        for (const urlItem of urlItems) {
          if (!ALLOWED_URL_MEDIA_TYPES.has(urlItem.mediaType)) continue;
          if (urlItem.isUploaded) continue;
          // Per-URL target overrides the form-level fallback.
          const urlTarget = urlItem.targetLanguage || targetLanguage;
          const urlPayload: Record<string, unknown> = {
            url: urlItem.downloadUrl,
            originalUrl: urlItem.originalUrl,
            fileName: urlItem.fileName,
            description: urlItem.description,
            folderId: urlItem.folderId,
            sourceLanguage: urlItem.sourceLanguage,
            duration: urlItem.duration,
          };
          if (mode === "translate" && urlTarget) {
            const { cost: translationCost, characters } = calculateTranslationCost(urlItem.duration);
            const dubbingCost = urlItem.enableDubbing
              ? calculateDubbingCost(urlItem.duration)
              : 0;
            urlPayload.processType = MediaProcessType.TRANSLATION;
            urlPayload.targetLanguage = urlTarget;
            urlPayload.enableDubbing = urlItem.enableDubbing;
            urlPayload.transcriptionCost = urlItem.amount ?? 0;
            urlPayload.translationCost = translationCost;
            urlPayload.dubbingCost = dubbingCost;
            urlPayload.characters = characters;
            urlPayload.amount = parseFloat(
              ((urlItem.amount ?? 0) + translationCost + dubbingCost).toFixed(2)
            );
          }
          // Credit mode: the per-item credit (from the estimate) is the media's real
          // cost — override the legacy dollar amount so media.amount reflects it.
          if (creditAmountByFileId) {
            urlPayload.amount = creditAmountByFileId[urlItem.id] ?? urlPayload.amount ?? 0;
          }
          const res = await apiClient.post<{ id: string; mediaType: string }>(
            `/media/${urlItem.mediaType}/upload/url`,
            urlPayload
          );

          if (res.success && res.data) {
            const safeUrl = (() => { try { const u = new URL(urlItem.originalUrl); return `${u.origin}${u.pathname}`; } catch { return "[invalid url]"; } })();
            track("create", "url_link", { url: safeUrl });
            processed.push({
              id: res.data.id,
              mediaType: res.data.mediaType,
            });
            setUrlItems((prev) =>
              prev.map((u) => (u.id === urlItem.id ? { ...u, isUploaded: true } : u))
            );
          }
        }

        // `retriable` files are included even though isValid is false — set
        // by uploadSingleFile's failure branch below for a transient
        // network failure or an offline-abort, so auto-resume-on-reconnect
        // (handleOnline, below) can re-include them. Without this, every
        // file killed by handleOffline's abort is permanently excluded from
        // auto-resume and needs a manual retry click (SPEAK-CLIENT-6T).
        const filesToUpload = files.filter((f) => (f.isValid || f.retriable) && !f.isUploaded);

        async function uploadSingleFile(file: UploadFileItem): Promise<void> {
          if (!user?._id) {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === file.id
                  ? { ...f, isValid: false, errorMessage: t("UPLOAD_FILE.UPLOAD_ERROR") }
                  : f
              )
            );
            return;
          }
          const fileName = sanitizeFileName(file.fileName, file.extension);

          setFiles((prev) =>
            prev.map((f) =>
              f.id === file.id
                ? {
                    ...f,
                    percent: 5,
                    // Clear any prior failure state as THIS attempt begins —
                    // matters for an auto-resumed retriable file, which
                    // still carries isValid:false/errorMessage/retriable
                    // from the failure that ended its last attempt (a
                    // manual retry already resets these via retryFile()).
                    isValid: true,
                    errorMessage: undefined,
                    retriable: false,
                  }
                : f
            )
          );

          const { promise, abort } = uploadFileToS3(
            file.file,
            {
              userId: user._id,
              filename: fileName,
              mediaType: file.mediaType as "audio" | "video" | "text" | "url",
              bucketId: subscription?.bucketId,
              maxBytes: pricing.isFreePlan ? FREE_PLAN_MAX_FILE_SIZE : paidPlanMaxFileSize,
              onProgress: (progress) => {
                setFiles((prev) =>
                  prev.map((f) =>
                    f.id === file.id ? { ...f, percent: progress.percent } : f
                  )
                );
              },
            },
          );

          abortRegistryRef.current.set(file.id, abort);
          let uploadResult: Awaited<typeof promise>;
          try {
            uploadResult = await promise;
          } finally {
            abortRegistryRef.current.delete(file.id);
          }

          if (uploadResult.status !== "success") {
            trackFailure("create", "media_file", {
              file_type: file.mediaType,
              file_size: file.file.size,
              source: "upload",
              upload_mode: "cognito",
              errorCategory: uploadResult.errorCategory ?? "unknown",
              error_reason: (uploadResult.error ?? "S3 upload failed").slice(0, 500),
              company_id: user?.companyId,
              cohort: subscription?.planId,
            });
            trackUploadFail(uploadResult.errorCategory ?? "unknown", file.mediaType, mode, goal);
            // Only a transient failure is worth an unattended retry — a
            // deterministic rejection (size_limit, content_type) will just
            // fail again identically, so it stays manual-retry-only.
            const retriable =
              uploadResult.status === "aborted" || uploadResult.errorCategory === "network";
            setFiles((prev) =>
              prev.map((f) =>
                f.id === file.id
                  ? {
                      ...f,
                      isValid: false,
                      errorMessage: uploadResult.error || t("UPLOAD_FILE.UPLOAD_ERROR"),
                      retriable,
                    }
                  : f
              )
            );
            // Resolve (not reject) so sibling files in the queue continue.
            return;
          }

          setFiles((prev) =>
            prev.map((f) =>
              f.id === file.id
                ? {
                    ...f,
                    percent: 100,
                    s3: {
                      bucket: uploadResult.bucket || "",
                      key: uploadResult.key || "",
                      location: uploadResult.location || "",
                    },
                  }
                : f
            )
          );

          const uploadPayload: UploadMediaPayload = {
            fileName: file.fileName,
            mediaType: file.mediaType,
            folderId: file.folderId || folderId,
            sourceLanguage: file.sourceLanguage || sourceLanguage,
            duration: file.duration,
            fileSize: file.file.size,
            extension: file.extension,
            aws: {
              bucket: uploadResult.bucket || "",
              key: uploadResult.key || "",
              location: uploadResult.location || "",
            },
            uploadSource: "web",
          };

          const fileTarget = file.targetLanguage || targetLanguage;
          if (mode === "translate" && fileTarget) {
            // Payload is GROSS per-file; the page-level effectiveTotalAmount
            // applies the freeCharacters quota for the dollar total shown to
            // the user. The server reconciles via amount.
            const { cost: translationCost, characters } = calculateTranslationCost(file.duration);
            const dubbingCost = file.enableDubbing
              ? calculateDubbingCost(file.duration)
              : 0;
            uploadPayload.processType = MediaProcessType.TRANSLATION;
            uploadPayload.targetLanguage = fileTarget;
            uploadPayload.enableDubbing = file.enableDubbing;
            uploadPayload.transcriptionCost = file.amount ?? 0;
            uploadPayload.translationCost = translationCost;
            uploadPayload.dubbingCost = dubbingCost;
            uploadPayload.characters = characters;
            uploadPayload.amount = parseFloat(
              ((file.amount ?? 0) + translationCost + dubbingCost).toFixed(2)
            );
          }

          // Credit mode: the per-file credit (from the estimate) is the media's real
          // cost — override the legacy dollar amount so media.amount reflects it.
          if (creditAmountByFileId) {
            uploadPayload.amount = creditAmountByFileId[file.id] ?? uploadPayload.amount ?? 0;
          }

          const serverRes = await apiClient.post<Array<{ id: string; mediaType: string }>>(
            "/media/upload/file",
            [uploadPayload],
          );

          if (serverRes.success && serverRes.data) {
            if (file.mediaCategory === "text") {
              track("create", "text_note", { file_type: file.mediaType, file_size: file.file.size, upload_mode: "cognito" });
            } else {
              trackFunnel("create", "media_file", { file_type: file.mediaType, file_size: file.file.size, source: "upload", upload_mode: "cognito" });
            }
            trackUploadComplete(file.mediaType, mode, goal);

            for (const item of serverRes.data) {
              processed.push({ id: item.id, mediaType: item.mediaType });
            }

            setFiles((prev) =>
              prev.map((f) =>
                f.id === file.id ? { ...f, isUploaded: true } : f
              )
            );
            resumeBackoffCountRef.current = 0;
          } else {
            // S3 PUT succeeded but the server rejected registration (success:
            // false) or returned a malformed success with no data. Without this
            // branch the file silently leaves the queue with no media doc, no
            // error, and no tracking. The user-facing message is the generic
            // i18n string — serverRes.error is a raw server pass-through and is
            // kept to the Sentry payload only, never rendered in the UI.
            trackFailure("create", "media_file", {
              file_type: file.mediaType,
              file_size: file.file.size,
              source: "upload",
              upload_mode: "cognito",
              errorCategory: "server_registration",
              error_reason: (serverRes.error ?? "Server registration failed").slice(0, 500),
              company_id: user?.companyId,
              cohort: subscription?.planId,
            });
            trackUploadFail("server_registration", file.mediaType, mode, goal);
            setFiles((prev) =>
              prev.map((f) =>
                f.id === file.id
                  ? {
                      ...f,
                      isValid: false,
                      errorMessage: t("UPLOAD_FILE.UPLOAD_ERROR"),
                    }
                  : f
              )
            );
          }
        }

        const queue = [...filesToUpload];
        const inFlight: Set<Promise<void>> = new Set();

        function enqueue(file: UploadFileItem): void {
          // Declared as a function so the finally callback captures `task` correctly.
          const task: Promise<void> = uploadSingleFile(file).finally(() => inFlight.delete(task));
          inFlight.add(task);
        }

        while (queue.length > 0 || inFlight.size > 0) {
          while (queue.length > 0 && inFlight.size < UPLOAD_CONCURRENCY) {
            enqueue(queue.shift()!);
          }
          if (inFlight.size > 0) {
            await Promise.race(inFlight);
          }
        }

        setProcessedFiles(processed);

        trackFunnel("upload", "s3_complete", {
          file_count: processed.length,
          failed_count: files.length + urlItems.length - processed.length,
          upload_mode: "cognito",
        });

        if (processed.length === 0) {
          trackActivity("upload.failure", { mode, sizeBucket: bucket, outcome: "failure", durationMs: Date.now() - uploadStartedAt });
          setStatus("error");
          setErrorMessage(t("UPLOAD_FILE.NO_FILES_ERROR"));
        } else {
          trackActivity("upload.success", { mode, sizeBucket: bucket, outcome: "success", durationMs: Date.now() - uploadStartedAt });
          // Files are in S3; payment (if any) is the caller's concern. Covered
          // paths transition immediately to "processing" via chargeAndProcess;
          // uncovered paths sit in "awaiting_payment" until the user pays or cancels.
          setStatus("awaiting_payment");
        }
        return processed;
      } catch (error) {
        trackFailure("create", "upload_fail", {
          error_reason: error instanceof Error ? error.message : "Unknown upload error",
          file_type: files[0]?.file?.type ?? "unknown",
          file_size: files[0]?.file?.size ?? 0,
          upload_mode: "cognito",
          errorCategory: "unknown",
          company_id: user?.companyId,
          cohort: subscription?.planId,
        });
        trackUploadFail("exception", undefined, mode, goal);
        trackActivity("upload.failure", { mode, sizeBucket: bucket, outcome: "error", durationMs: Date.now() - uploadStartedAt });
        setStatus("error");
        setErrorMessage(
          error instanceof Error
            ? error.message
            : t("UPLOAD_FILE.UPLOAD_ERROR")
        );
        return [];
      }
    } finally {
      isUploadingRef.current = false;
    }
  }, [files, urlItems, user, subscription?.bucketId, subscription?.planId, paidPlanMaxFileSize, folderId, sourceLanguage, t, mode, targetLanguage, goal]);

  // Closing or reloading the tab kills in-flight transfers, so that is the abandon signal.
  useEffect(() => {
    if (status !== "uploading" || typeof window === "undefined") return undefined;
    const handlePageHide = () => trackUploadAbandon("uploading", mode, goal);
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [status, mode, goal]);

  // uploadFilesOnly is recreated whenever `files` changes; the long-lived
  // online/offline listeners below read this ref instead of closing over
  // uploadFilesOnly directly, so a reconnect always invokes the CURRENT
  // version (with the current file list) rather than whichever version was
  // current when those listeners were last (re)installed.
  const uploadFilesOnlyRef = useRef(uploadFilesOnly);
  useEffect(() => {
    uploadFilesOnlyRef.current = uploadFilesOnly;
  }, [uploadFilesOnly]);

  /**
   * Process-and-finalize variant that throws instead of swallowing errors,
   * so the caller can distinguish "charged but processing failed" from
   * success and fire the PAYMENT_OK_PROCESS_FAILED toast + Sentry signal.
   */
  const chargeAndProcess = useCallback(async (processed: ProcessedFile[]) => {
    setStatus("processing");

    const processRes = await apiClient.post<
      Array<{ mediaId: string; mediaType: string; name?: string }>
    >("/media/process/file", processed.map((p) => ({ ...p, isPaid: true })));

    if (!processRes.success || !processRes.data) {
      setStatus("error");
      setErrorMessage(t("UPLOAD_FILE.PROCESS_FAILED_ERROR"));
      throw new Error(processRes.error ?? "process_failed");
    }

    setUploadedMediaIds(
      processRes.data.map((m) => ({
        mediaId: m.mediaId,
        name: m.name || "file",
      })),
    );

    // Kicks off the AI pipeline. Without this the doc stays in "preparing" forever.
    const analyzePayload = processRes.data.map((m) => ({
      mediaId: m.mediaId,
      mediaType: m.mediaType,
    }));
    const analyzeRes = await apiClient.post<unknown>("/media/analyze", analyzePayload);

    if (!analyzeRes.success) {
      // Charge succeeded and the doc exists — surface a distinct error so the
      // user knows the upload landed but analysis didn't kick off.
      setStatus("error");
      setErrorMessage(
        t(
          "UPLOAD_FILE.ANALYZE_FAILED_ERROR",
          "Payment succeeded and your file is uploaded, but analysis didn't start. Contact support.",
        ),
      );
      throw new Error(analyzeRes.error ?? "analyze_failed");
    }

    trackActivity("transcription.start", { mode });
    setStatus("complete");
  }, [t, mode]);

  /**
   * Legacy wrapper kept for use-first-action.ts. The upload page calls
   * uploadFilesOnly + its own branching directly.
   */
  const submitUpload = useCallback(async () => {
    const processed = await uploadFilesOnly();
    if (processed.length === 0) return;
    const { hasFreeMinutes, hasFreeTextNotes, displayAmount, isDeductFromBalance } = pricingSummary;
    if (hasFreeMinutes || hasFreeTextNotes || displayAmount === 0 || isDeductFromBalance) {
      await chargeAndProcess(processed);
    } else {
      setStatus("error");
      setErrorMessage(t("UPLOAD_FILE.PAYMENT_REQUIRED_ERROR"));
    }
  }, [uploadFilesOnly, chargeAndProcess, pricingSummary, t]);

  const cancelFile = useCallback((id: string) => {
    abortRegistryRef.current.get(id)?.();
  }, []);

  const cancelAllUploads = useCallback(() => {
    abortRegistryRef.current.forEach((abort) => abort());
    abortRegistryRef.current.clear();
  }, []);

  const retryFile = useCallback(
    async (id: string) => {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? { ...f, isValid: true, errorMessage: undefined, percent: 0, isUploaded: false }
            : f
        )
      );
      await uploadFilesOnly();
    },
    [uploadFilesOnly]
  );

  // Semantically distinct from "cancel", which aborts in-flight uploads.
  const skipFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // Auto-resume on reconnect with exponential backoff (2s, 4s, 8s, 16s cap).
  //
  // Gating on `status === "uploading"` here used to look right but never
  // actually fired: uploadFilesOnly's queue-drain loop resolves (not
  // rejects) a failed/aborted file so siblings continue, so by the time it
  // returns — near-instantly, well before a real network reconnect — status
  // has already moved to "error"/"awaiting_payment". A real `online` event
  // arrives long after that, so the old check was always false. Gate on
  // whether there's actually retriable work instead, read fresh off
  // filesRef (this effect installs its listeners once, on mount, so its own
  // closures can't see later state — that's what filesRef/uploadFilesOnlyRef
  // are for).
  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
      const hasRetriableFiles = filesRef.current.some((f) => f.retriable);
      if (wasOfflineRef.current && hasRetriableFiles) {
        wasOfflineRef.current = false;
        const backoffMs = Math.min(2 ** resumeBackoffCountRef.current * 1000, 16000);
        resumeBackoffCountRef.current += 1;
        resumeTimeoutRef.current = window.setTimeout(() => {
          resumeTimeoutRef.current = null;
          uploadFilesOnlyRef.current().catch(() => {
            // Failure increments backoff; the next reconnect waits longer.
          });
        }, backoffMs);
      } else {
        // Nothing retriable — e.g. the disconnect happened before any file
        // reached the abort/failure path — so don't flip the latch; a LATER
        // offline/online cycle should still be able to trigger a resume.
        wasOfflineRef.current = false;
      }
    }
    function handleOffline() {
      setIsOnline(false);
      wasOfflineRef.current = true;
      // Abort in-flight uploads so they fail cleanly and retry on reconnect.
      abortRegistryRef.current.forEach((abort) => abort());
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
        if (resumeTimeoutRef.current !== null) {
          clearTimeout(resumeTimeoutRef.current);
          resumeTimeoutRef.current = null;
        }
      };
    }
    return undefined;
  }, []);

  // Status-transition helpers — expose the state machine to callers without
  // leaking raw setStatus. Each maps 1:1 to a UploadStatus variant.
  const enterFilesSelected = useCallback(() => {
    setStatus((prev) => (prev === "idle" ? "files_selected" : prev));
  }, []);

  const enterTopupRequired = useCallback(() => {
    setStatus("topup_required");
  }, []);

  const enterAddPaymentMethod = useCallback(() => {
    setStatus("add_payment_method");
  }, []);

  const enterChargeConfirm = useCallback(() => {
    setStatus("charge_confirm");
  }, []);

  const enterChargeProcessing = useCallback(() => {
    setStatus("charge_processing");
  }, []);

  const enterChargeFailed = useCallback(() => {
    setStatus("charge_failed");
  }, []);

  const enterDirectDepositGate = useCallback(() => {
    setStatus("direct_deposit_gate");
  }, []);

  const reset = useCallback(() => {
    uploadStartTrackedRef.current = false;
    setFiles([]);
    setUrlItems([]);
    setStatus("idle");
    setErrorMessage("");
    setProcessedFiles([]);
    setUploadedMediaIds([]);
  }, []);

  const goToFolder = useCallback(() => {
    navigate(folderId ? `/folder/${folderId}` : "/home");
  }, [navigate, folderId]);

  const goToFile = useCallback(
    (mediaId: string) => {
      if (!folderId) { navigate("/home"); return; }
      navigate(`/folder/${folderId}/insight/${mediaId}`);
    },
    [navigate, folderId]
  );

  return {
    files,
    urlItems,
    status,
    errorMessage,
    folderId,
    sourceLanguage,
    subscription,
    pricingSummary,
    processedFiles,
    uploadedMediaIds,
    isLoading: pricing.isLoading,
    currentBalance,
    isOnline,
    validateBatch,

    addFiles,
    importFromUrl,
    removeFile,
    removeUrlItem,
    updateFile,
    updateUrlItem,
    setFolderId,
    submitUpload,
    uploadFilesOnly,
    chargeAndProcess,
    processMedia,
    reset,
    goToFolder,
    goToFile,
    setErrorMessage,
    cancelFile,
    cancelAllUploads,
    retryFile,
    skipFile,

    enterFilesSelected,
    enterTopupRequired,
    enterAddPaymentMethod,
    enterChargeConfirm,
    enterChargeProcessing,
    enterChargeFailed,
    enterDirectDepositGate,

    urlImport,
  };
}
